import React, { useEffect, useMemo, useState } from "react"
import { Button, Checkbox, Input } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowDown,
  faArrowUp,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons"
import {
  CategoryDisplay,
  DataTile,
  isDataTile,
} from "features/Dashboard/types/dashboardDocument"
import {
  runtimeModel,
  useDashboardStore,
} from "features/Dashboard/runtime/dashboardStore"
import {
  DisplayRow,
  displayRows,
  orderDisplayRows,
} from "features/Dashboard/runtime/categoryDisplay"
import {
  carryColour,
  combineIntoNet,
  inCategoryOrder,
} from "features/Dashboard/runtime/measureEdits"
import NetHint from "./NetHint.component"

/**
 * A row's shown name, written when the field loses focus or on Enter. An
 * empty field, or the row's own label, drops the new name.
 */
const RowName: React.FC<{
  row: DisplayRow
  name: string | undefined
  hidden: boolean
  onCommit: (name: string | null) => void
}> = ({ row, name, hidden, onCommit }) => {
  const [draft, setDraft] = useState(name ?? row.label)
  useEffect(() => setDraft(name ?? row.label), [name, row.label])
  const commit = (): void => {
    const text = draft.trim()
    const next = text && text !== row.label ? text : null
    if (!text) setDraft(row.label)
    if (next !== (name ?? null)) onCommit(next)
  }
  return (
    <Input
      size="small"
      className="dq-catlist__name"
      aria-label={`Name for ${row.label}`}
      title={name ? `Was ${row.label}` : undefined}
      placeholder={row.label}
      value={draft}
      disabled={hidden}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onPressEnter={commit}
    />
  )
}

/**
 * The rows a tile shows, in the order it shows them, from its last result
 * and its measures: move a row up or down (among the categories, or among
 * the measures), hide or show it, give it another name, or tick categories
 * and combine them into a net that stands in for them. Display only: every
 * write goes through `updateTileView`, so the tile does not run again. The
 * eye is the one way to hide a row. A renamed row takes its dashboard
 * colour to its new name, and the old name keeps it (see `carryColour`).
 */
const CategoryDisplayEditor: React.FC<{ tile: DataTile; pageId: string }> = ({
  tile,
  pageId,
}) => {
  const updateTileView = useDashboardStore((s) => s.updateTileView)
  const theme = useDashboardStore((s) => s.document.theme)
  const setTheme = useDashboardStore((s) => s.setTheme)
  const colourMap = theme.categoryColours
  // A repeated tile lists from its first panel: every panel has the same
  // rows and columns.
  const model = useDashboardStore((s) => runtimeModel(s.tiles[tile.id]))
  const [ticked, setTicked] = useState<string[]>([])
  const [netName, setNetName] = useState("")
  const [status, setStatus] = useState<string | null>(null)
  const { categories } = tile
  const rows = useMemo(
    () => orderDisplayRows(displayRows(tile, model), categories.order),
    [tile, model, categories.order]
  )

  if (!model && tile.measures.length === 0) {
    return (
      <p className="dq-field__hint">Run the tile to list its categories.</p>
    )
  }

  const hidden = new Set(categories.hidden)
  const edit = (fn: (d: CategoryDisplay) => CategoryDisplay): void =>
    updateTileView(pageId, tile.id, (t) =>
      isDataTile(t) ? { ...t, categories: fn(t.categories) } : t
    )
  const move = (index: number, by: -1 | 1): void => {
    const j = index + by
    if (j < 0 || j >= rows.length) return
    if (rows[j].measure !== rows[index].measure) return
    const ids = rows.map((r) => r.id)
    ;[ids[index], ids[j]] = [ids[j], ids[index]]
    edit((d) => ({ ...d, order: ids }))
  }
  const toggleHidden = (id: string): void =>
    edit((d) => ({
      ...d,
      hidden: d.hidden.includes(id)
        ? d.hidden.filter((h) => h !== id)
        : [...d.hidden, id],
    }))
  // A row's dashboard colour goes with it from the name it showed to the new.
  const rename = (row: DisplayRow, name: string | null): void => {
    const before = Object.hasOwn(categories.labels, row.id)
      ? categories.labels[row.id]
      : row.label
    edit((d) => {
      const labels = Object.fromEntries(
        Object.entries(d.labels).filter(([id]) => id !== row.id)
      )
      return { ...d, labels: name ? { ...labels, [row.id]: name } : labels }
    })
    const colours = carryColour(colourMap, before, name ?? row.label)
    if (colours) setTheme({ ...theme, categoryColours: colours })
  }
  const categoryRows = rows.filter((r) => !r.measure)
  const members = inCategoryOrder(categoryRows, ticked)
  const combine = (): void => {
    let label = ""
    updateTileView(pageId, tile.id, (t) => {
      if (!isDataTile(t)) return t
      const next = combineIntoNet(t, members, netName)
      label = next.tile.measures.find((m) => m.id === next.netId)?.label ?? ""
      return next.tile
    })
    setTicked([])
    setNetName("")
    setStatus(
      `Added the net ${label}. Its ${members.length} categories are hidden.`
    )
  }
  const touched =
    Boolean(categories.order?.length) ||
    categories.hidden.length > 0 ||
    Object.keys(categories.labels).length > 0
  const firstMeasure = rows.findIndex((r) => r.measure)

  return (
    <div className="dq-catlist">
      <ol className="dq-catlist__list" aria-label="Rows">
        {rows.map((row, i) => {
          const isHidden = hidden.has(row.id)
          const first = i === 0 || rows[i - 1].measure !== row.measure
          const last = i === rows.length - 1 || rows[i + 1].measure !== row.measure
          return (
            <li
              key={row.id}
              className="dq-catlist__row"
              data-hidden={isHidden || undefined}
              data-first-measure={i === firstMeasure || undefined}
            >
              {row.measure ? (
                <span className="dq-catlist__tick" aria-hidden />
              ) : (
                <Checkbox
                  className="dq-catlist__tick"
                  aria-label={`Select ${row.label}`}
                  checked={ticked.includes(row.id)}
                  onChange={(e) =>
                    setTicked((prev) =>
                      e.target.checked
                        ? [...prev, row.id]
                        : prev.filter((x) => x !== row.id)
                    )
                  }
                />
              )}
              <RowName
                row={row}
                name={categories.labels[row.id]}
                hidden={isHidden}
                onCommit={(name) => rename(row, name)}
              />
              {row.measure && <span className="dq-catlist__kind">Measure</span>}
              <Button
                size="small"
                type="text"
                aria-label={`Move ${row.label} up`}
                disabled={first}
                icon={<FontAwesomeIcon icon={faArrowUp} />}
                onClick={() => move(i, -1)}
              />
              <Button
                size="small"
                type="text"
                aria-label={`Move ${row.label} down`}
                disabled={last}
                icon={<FontAwesomeIcon icon={faArrowDown} />}
                onClick={() => move(i, 1)}
              />
              <Button
                size="small"
                type="text"
                className="dq-catlist__eye"
                aria-label={`Hide ${row.label}`}
                aria-pressed={isHidden}
                title={isHidden ? "Hidden: click to show" : "Hide"}
                icon={<FontAwesomeIcon icon={isHidden ? faEyeSlash : faEye} />}
                onClick={() => toggleHidden(row.id)}
              />
            </li>
          )
        })}
      </ol>
      {categoryRows.length > 1 && (
        <div className="dq-catlist__combine">
          <Input
            size="small"
            aria-label="Net name"
            placeholder="Net"
            value={netName}
            onChange={(e) => setNetName(e.target.value)}
          />
          <Button
            size="small"
            disabled={members.length < 2}
            onClick={combine}
          >
            Combine selected into a net
          </Button>
        </div>
      )}
      {members.length > 0 && <NetHint />}
      {status && (
        <p className="dq-field__hint dq-catlist__status" role="status">
          {status}
        </p>
      )}
      {touched && (
        <Button
          size="small"
          type="link"
          className="dq-catlist__reset"
          onClick={() => edit(() => ({ hidden: [], labels: {} }))}
        >
          Reset order, visibility and names
        </Button>
      )}
    </div>
  )
}

export default CategoryDisplayEditor
